import * as React from 'react';
import '../../../../assets/css/tabbed-single-select.css';
import '../../../../assets/css/tabbed-multi-select.css';
import * as _ from 'lodash';
import { Button } from 'semantic-ui-react';

interface ISource {
    label: string,
    noResultsLabel?: string,
    noSearchResultsLabel?: string,
    items: {
        value: string | number,
        label: string
    }[]
}

interface ITabbedMultiSelectState {
    id: string,
    defaultValues?: (string | number)[],
    showModal: boolean,
    itemsSelected: { tabId: number, value: string | number, label: string }[],
    activeTab: number,
    sources: ISource[],
    required: boolean,
    disabled: boolean,
    searchText: string
}

interface ITabbedMultiSelectProps {
    id: string,
    defaultValues?: (string | number)[],
    sources: ISource[],
    onChange?: (values: any[], id?: any) => void,
    required: boolean,
    disabled: boolean,
    'data-testid'?: string
}

interface ITabbedMultiSelectState {
    id: string,
    defaultValues?: (string | number)[],
    showModal: boolean,
    itemsSelected: { tabId: number, value: string | number, label: string }[],
    activeTab: number,
    sources: ISource[],
    required: boolean,
    disabled: boolean,
    searchText: string,
    showOnlySelected: boolean
}

export class TabbedMultiSelect extends React.Component<ITabbedMultiSelectProps, ITabbedMultiSelectState> {
    divRef: any;
    private _isMounted: boolean = false;
    private _timeouts: any[] = [];
    
    constructor(props) {
        super(props)
        this.state = {
            id: this.props.id,
            showModal: false,
            itemsSelected: [],
            activeTab: 0,
            sources: this.props.sources || [],
            defaultValues: this.props.defaultValues || [],
            required: this.props.required || false,
            disabled: this.props.disabled || false,
            searchText: '',
            showOnlySelected: false
        }

        this.divRef = React.createRef()
        this.setDefaultValues()
    }

    componentDidUpdate(prevProps: Readonly<ITabbedMultiSelectProps>, prevState: Readonly<ITabbedMultiSelectState>, snapshot?: any): void {
        if (!this._isMounted) return;
        
        if (this.props.sources !== prevProps.sources) {
            this.setState({ sources: this.props.sources || [] }, () => {
                this.setDefaultValues()
            })
        }
        if (JSON.stringify(this.props.defaultValues) !== JSON.stringify(prevProps.defaultValues)) {
            this.setState({ defaultValues: this.props.defaultValues || [] }, () => {
                this.setDefaultValues()
            })
        }
        if (this.props.required !== prevProps.required) {
            this.setState({ required: this.props.required || false })
        }
        if (this.props.disabled !== prevProps.disabled) {
            this.setState({ disabled: this.props.disabled || false })
        }
    }

    setDefaultValues = () => {
        if (this.state.defaultValues && this.state.defaultValues.length > 0) {
            let selectedItems: { tabId: number, value: string | number, label: string }[] = [];
            
            // Find the first tab that has any of the default values
            let firstTabWithDefaults = -1;
            this.state.sources.forEach((source: any, tabIndex: any) => {
                source.items.forEach((item: any) => {
                    if (this.state.defaultValues.indexOf(item.value) !== -1) {
                        if (firstTabWithDefaults === -1) {
                            firstTabWithDefaults = tabIndex;
                        }
                        // Only add items from the first tab that has defaults
                        if (tabIndex === firstTabWithDefaults) {
                            selectedItems.push({ ...item, tabId: tabIndex });
                        }
                    }
                });
            });
            
            if (selectedItems.length > 0) {
                if (this._isMounted) {
                    // Set activeTab to the tab that contains the selected items
                    this.setState({ 
                        itemsSelected: selectedItems,
                        activeTab: firstTabWithDefaults !== -1 ? firstTabWithDefaults : 0
                    });
                }
                this.props.onChange && this.props.onChange(selectedItems, this.state.id);
            }
        } else {
            if (this._isMounted) {
                this.setState({ itemsSelected: [] });
            }
            this.props.onChange && this.props.onChange([], this.state.id);
        }
    }

    handleClickOutside = (event: any) => {
        if (this.divRef.current && !this.divRef.current.contains(event.target)) {
            if (this._isMounted) {
                this.setState({ showModal: false, searchText: '', showOnlySelected: false })
            }
        }
    }

    componentDidMount() {
        this._isMounted = true;
        document.addEventListener('click', this.handleClickOutside)
        
        // Set default values now that component is mounted
        this.setDefaultValues();
    }

    componentWillUnmount() {
        this._isMounted = false;
        document.removeEventListener('click', this.handleClickOutside)
        
        // Clear all pending timeouts
        this._timeouts.forEach(timeout => clearTimeout(timeout));
        this._timeouts = [];
    }

    // Helper method to safely set state after a timeout
    private safeSetTimeout = (callback: () => void, delay: number = 0) => {
        const timeout = setTimeout(() => {
            if (this._isMounted) {
                callback();
            }
        }, delay);
        this._timeouts.push(timeout);
        return timeout;
    }

    handleChange = (e: any) => {
        const value = e.target.value;
        // If user starts typing and we're in "show only selected" mode, reset to normal mode
        const shouldResetShowOnlySelected = this.state.showOnlySelected && value.trim() !== '';
        this.setState({ 
            searchText: value,
            showOnlySelected: shouldResetShowOnlySelected ? false : this.state.showOnlySelected
        });
    }

    toggleShowOnlySelected = () => {
        this.setState(prevState => ({ 
            showOnlySelected: !prevState.showOnlySelected,
            showModal: true,
            searchText: ''
        }));
    }

    handleSelect = (tabId: number, item: any, event?: any) => {
        // Prevent event bubbling to avoid triggering handleClickOutside
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        
        // Check if user is trying to select from a different tab than current selections
        if (this.state.itemsSelected.length > 0 && this.state.itemsSelected[0].tabId !== tabId) {
            // Clear existing selections and start fresh with new tab
            const newSelection = [{ ...item, tabId }];
            this.setState({ itemsSelected: newSelection });
            this.props.onChange && this.props.onChange(newSelection, this.state.id);
            return;
        }

        const isAlreadySelected = this.state.itemsSelected.some(
            selected => selected.value === item.value && selected.tabId === tabId
        );
        
        if (isAlreadySelected) {
            // Remove item if already selected
            const filtered = this.state.itemsSelected.filter(
                selected => !(selected.value === item.value && selected.tabId === tabId)
            );
            // Always keep dropdown open when unchecking items
            // If no items left and we're in "show only selected" mode, reset to normal mode
            const shouldResetShowOnlySelected = filtered.length === 0 && this.state.showOnlySelected;
            this.setState({ 
                itemsSelected: filtered, 
                searchText: '',
                showModal: true, // Always keep dropdown open when unchecking
                showOnlySelected: shouldResetShowOnlySelected ? false : this.state.showOnlySelected
            });
            
            // Use setTimeout to ensure the dropdown stays open after any potential event bubbling
            this.safeSetTimeout(() => {
                this.setState({ showModal: true });
            }, 0);
            
            this.props.onChange && this.props.onChange(filtered, this.state.id);
        } else {
            // Add new item
            const newSelection = [...this.state.itemsSelected, { ...item, tabId }];
            this.setState({ itemsSelected: newSelection, searchText: '' });
            this.props.onChange && this.props.onChange(newSelection, this.state.id);
        }
    }

    handleDelete = (itemToDelete?: { tabId: number, value: string | number, label: string }) => {
        if (itemToDelete) {
            // Remove specific item
            const filtered = this.state.itemsSelected.filter(
                item => !(item.value === itemToDelete.value && item.tabId === itemToDelete.tabId)
            );
            
            // Determine the new active tab
            let newActiveTab = this.state.activeTab;
            
            if (filtered.length === 0) {
                // No more selections, reset to first tab and keep dropdown open
                newActiveTab = 0;
                this.setState({ 
                    itemsSelected: filtered, 
                    activeTab: newActiveTab,
                    showModal: true // Keep dropdown open when all items are removed
                });
            } else {
                // Check if current active tab still has selections
                const hasItemsInActiveTab = filtered.some(item => item.tabId === this.state.activeTab);
                if (!hasItemsInActiveTab) {
                    // Current active tab has no more items, switch to the tab that still has items
                    newActiveTab = filtered[0].tabId;
                }
                this.setState({ itemsSelected: filtered, activeTab: newActiveTab });
            }
            
            this.props.onChange && this.props.onChange(filtered, this.state.id);
        } else {
            // Clear all selections and reset active tab to 0, keep dropdown open
            this.setState({ 
                itemsSelected: [], 
                activeTab: 0,
                showModal: true // Keep dropdown open when clearing all
            });
            this.props.onChange && this.props.onChange([], this.state.id);
        }
    }

    activateTab = ({ target }) => {
        const {
            dataset: { id }
        } = target;

        this.setState({ activeTab: id })
    }

    on_select_all = () => {
        if (this.state.sources.length === 0 || this.state.activeTab < 0 || this.state.activeTab >= this.state.sources.length) {
            return;
        }

        const currentSource = this.state.sources[this.state.activeTab];
        const newSelections: { tabId: number, value: string | number, label: string }[] = [];
        
        // Add all items from the current active tab
        currentSource.items.forEach((item: any) => {
            newSelections.push({ ...item, tabId: this.state.activeTab });
        });

        this.setState({ 
            itemsSelected: newSelections,
            searchText: ''
        });
        
        this.props.onChange && this.props.onChange(newSelections, this.state.id);
    }

    on_reset = () => {
        this.setState({ 
            itemsSelected: [],
            searchText: '',
            showOnlySelected: false
        });
        
        this.props.onChange && this.props.onChange([], this.state.id);
    }

    renderItems = (items: any) => {
        // Safety check for activeTab - prevent infinite loops
        if (this.state.activeTab < 0 || this.state.activeTab >= this.state.sources.length) {
            // Use setTimeout to avoid infinite re-renders
            this.safeSetTimeout(() => {
                this.setState({ activeTab: 0 });
            }, 0);
            return null;
        }
        
        // Additional safety check - ensure activeTab points to a valid tab with items
        if (this.state.sources[this.state.activeTab] && this.state.sources[this.state.activeTab].items.length === 0) {
            // If current active tab has no items, find the first tab with items
            const firstTabWithItems = this.state.sources.findIndex(source => source.items.length > 0);
            if (firstTabWithItems !== -1) {
                this.safeSetTimeout(() => {
                    this.setState({ activeTab: firstTabWithItems });
                }, 0);
                return null;
            }
        }

        // Get selected items for this tab
        const selectedItemsInTab = this.state.itemsSelected.filter(
            selected => selected.tabId === this.state.activeTab
        );

        // If showOnlySelected is true, only show selected items
        if (this.state.showOnlySelected) {
            if (selectedItemsInTab.length === 0) {
                return (<div className='tabbed-dropdown-item'>No items selected in this tab</div>);
            }
            
            return (
                <>
                    {selectedItemsInTab.map((item, index) => (
                        <div 
                            key={`selected-only-${index}`} 
                            className='tabbed-dropdown-item tabbed-dropdown-item-selected tabbed-multi-select-item-selected'
                            onClick={(e) => { this.handleSelect(this.state.activeTab, item, e) }}
                            title={item.label}
                        >
                            <input 
                                type="checkbox" 
                                checked={true} 
                                readOnly 
                                className='tabbed-multi-select-checkbox'
                            />
                            {item.label}
                        </div>
                    ))}
                </>
            );
        }

        // Filter items by search text and show all items in their original order
        const filteredItems = items.filter((item: any) => {
            // Apply search filter if there's search text
            if (this.state.searchText.trim()) {
                return item.label.toLowerCase().includes(this.state.searchText.toLowerCase());
            }
            
            return true; // Show all items when no search
        });

        // If no items to show, display message
        if (filteredItems.length === 0) {
            return (<div className='tabbed-dropdown-item'>{this.state.sources[this.state.activeTab].noSearchResultsLabel || 'No items found'}</div>)
        }

        return (
            <>
                {/* Show all items in their original order with selection state */}
                {filteredItems.map((item, index) => {
                    const isSelected = this.state.itemsSelected.some(
                        selected => selected.value === item.value && selected.tabId === this.state.activeTab
                    );
                    
                    return (
                        <div 
                            key={`item-${index}`} 
                            className={`tabbed-dropdown-item ${isSelected ? 'tabbed-dropdown-item-selected tabbed-multi-select-item-selected' : ''}`}
                            onClick={(e) => { this.handleSelect(this.state.activeTab, item, e) }}
                            title={item.label}
                        >
                            <input 
                                type="checkbox" 
                                checked={isSelected} 
                                readOnly 
                                className='tabbed-multi-select-checkbox'
                            />
                            {item.label}
                        </div>
                    );
                })}
            </>
        )
    }

    renderSelectedItems = () => {
        if (this.state.itemsSelected.length === 0) {
            return null;
        }

        return (
            <div className='tabbed-multi-select-selected-items-container'>
                {this.state.itemsSelected.map((item, index) => (
                    <div 
                        key={index} 
                        className="ui label small tabbed-multi-select-selected-item-tag"
                    >
                        <span className='tabbed-multi-select-selected-item-label'>{item.label}</span>
                        <i 
                            className="close small icon tabbed-multi-select-selected-item-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleDelete(item);
                            }}
                        />
                    </div>
                ))}
            </div>
        );
    }

    render() {
        return (
            <div>
                <div id={this.state.id} ref={this.divRef} className={['ui dropdown tabbed-dropdown tabbed-multi-select-container', this.state.required && 'tabbed-dropdown-required'].filter(Boolean).join(' ')} data-testid={this.props['data-testid']}>
                    <div className='tabbed-multi-select-content'>
                        <div className='tabbed-multi-select-row'>
                            <div className='tabbed-multi-select-input-container'>
                                {/* Eye icon and count container - positioned before the input */}
                                {this.state.itemsSelected.length > 0 && (
                                    <span
                                        onClick={this.toggleShowOnlySelected}
                                        className={`tabbed-multi-select-eye-toggle ${this.state.showOnlySelected ? 'tabbed-multi-select-eye-toggle--active' : 'tabbed-multi-select-eye-toggle--normal'}`}
                                    >
                                        <i className="eye icon tabbed-multi-select-eye-icon"></i>
                                        {this.state.itemsSelected.length} Selected
                                    </span>
                                )}
                                <input 
                                    type="text" 
                                    placeholder="Search..."
                                    disabled={this.state.disabled} 
                                    className={['tabbed-dropdown-input tabbed-multi-select-search-input', this.state.required && 'tabbed-dropdown-input-required'].filter(Boolean).join(' ')} 
                                    onFocus={() => { 
                                        this.setState({ 
                                            showModal: true,
                                            // Only switch to first tab if dropdown was collapsed AND no items selected
                                            activeTab: !this.state.showModal && (!this.state.itemsSelected || this.state.itemsSelected.length === 0) ? 0 : this.state.activeTab
                                        })
                                    }}
                                    onChange={this.handleChange}
                                    value={this.state.searchText}
                                />
                            </div>
                            <div className={`${this.state.disabled ? 'tabbed-dropdown-icon-disabled' : 'tabbed-dropdown-icon'} ${this.state.required ? 'tabbed-dropdown-icon-required' : ''}`} onClick={() => { 
                                if (!this.state.disabled) {
                                    this.setState((prevState) => ({ 
                                        showModal: !prevState.showModal,
                                        // Only switch to first tab if dropdown was collapsed AND no items selected AND we're opening it
                                        activeTab: !prevState.showModal && (!this.state.itemsSelected || this.state.itemsSelected.length === 0) ? 0 : this.state.activeTab,
                                        // Reset showOnlySelected when closing the dropdown
                                        showOnlySelected: prevState.showModal ? false : prevState.showOnlySelected
                                    }))
                                }
                            }}>
                                <i aria-hidden="true" className="chevron down small icon"></i>
                            </div>
                        </div>
                        
                        {/* Remove the selected items count display with eye icon */}
                        
                        {/* Remove the selected items display */}
                        
                        <div className={`menu transition tabbed-multi-select-menu ${this.state.showModal ? 'visible' : ''}`}>
                            <div style={{ width: `${this.divRef && this.divRef.current && this.divRef.current.clientWidth ? `${this.divRef && this.divRef.current && this.divRef.current.clientWidth}px` : 'auto'}` }}>
                                <div className='tabbed-multi-select-menu-content'>
                                    {this.state.sources.length > 0 &&
                                        <>
                                            <div className='tabbed-multi-select-tabs-container'>
                                                {this.state.sources.map((source, index) => {
                                                    // Only disable tabs when there are actual selections
                                                    const isTabDisabled = this.state.itemsSelected.length > 0 && this.state.itemsSelected[0].tabId !== index;
                                                    const tabClass = index == this.state.activeTab ? 'tabbed-dropdown-tab-active' : 'tabbed-dropdown-tab';
                                                    const disabledClass = isTabDisabled ? 'tabbed-dropdown-tab-disabled' : '';
                                                    
                                                    return (
                                                        <div 
                                                            key={index} 
                                                            className={`${tabClass} ${disabledClass} ${isTabDisabled ? 'tabbed-multi-select-tab-disabled' : 'tabbed-multi-select-tab-enabled'}`}
                                                            onClick={() => { 
                                                                if (!isTabDisabled) {
                                                                    this.setState({ activeTab: index });
                                                                }
                                                            }}
                                                        >
                                                            {source.label}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className='tabbed-multi-select-items-container'>
                                                                                                 {(() => {
                                                     // Safety check for activeTab
                                                     if (this.state.activeTab < 0 || this.state.activeTab >= this.state.sources.length) {
                                                         return (
                                                             <div className='tabbed-dropdown-item tabbed-multi-select-loading-text'>
                                                                 Loading...
                                                             </div>
                                                         );
                                                     }
                                                     
                                                     // When no items are selected, show all tabs normally
                                                     if (this.state.itemsSelected.length === 0) {
                                                         if (this.state.sources[this.state.activeTab].items.length == 0) {
                                                             return (<div className='tabbed-dropdown-item'>{this.state.sources[this.state.activeTab].noResultsLabel || 'Tab has no results'}</div>);
                                                         } else {
                                                             return this.renderItems(this.state.sources[this.state.activeTab].items);
                                                         }
                                                     }
                                                     
                                                     const isCurrentTabDisabled = this.state.itemsSelected[0].tabId !== this.state.activeTab;
                                                     
                                                     if (isCurrentTabDisabled) {
                                                         return (
                                                             <div className='tabbed-dropdown-item tabbed-multi-select-disabled-text'>
                                                                 Clear selections from other tab to access this tab
                                                             </div>
                                                         );
                                                     } else if (this.state.sources[this.state.activeTab].items.length == 0) {
                                                         return (<div className='tabbed-dropdown-item'>{this.state.sources[this.state.activeTab].noResultsLabel || 'Tab has no results'}</div>);
                                                     } else {
                                                         return this.renderItems(this.state.sources[this.state.activeTab].items);
                                                     }
                                                 })()}
                                            </div>
                                        </>
                                    }
                                    <div className="tabbed-multi-select-footer">
                                        <Button type='button' onClick={this.on_select_all} className="tabbed-multi-select-footer-button select-all">Select All</Button>
                                        <Button type='button' onClick={this.on_reset} className="tabbed-multi-select-footer-button reset">Reset</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default TabbedMultiSelect
